/* global google */
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { useState, forwardRef, InputHTMLAttributes, useRef, useEffect } from 'react';
import { AriaTextFieldProps } from 'react-aria';
import clsx from 'clsx';
import styles from './GooglePlaces.module.css';

// Hoisted so the array identity is stable across renders — passing a fresh array
// to useLoadScript on every render forces the Maps script to reload and warns.
const LIBRARIES: 'places'[] = ['places'];

export interface GooglePlacesProps extends AriaTextFieldProps {
  className?: string;
  inputFieldClassName?: string;
  size?: 'medium' | 'large';
  label?: string;
  labelClassName?: string;
  isFilled?: string | boolean;
  placeholder?: string;
  inputExternalProps?: InputHTMLAttributes<HTMLInputElement>;
  onSuccess?: (selectedAddress: { fullAddress: string } & SelectedAddress) => void;
  onError?: (error: string) => void;
  availableCountry?: string | null;
}

export interface SelectedAddress {
  country: string;
  countryIso: string;
  state: string;
  town: string;
  street: string;
  postcode: string;
  unitNumber: string;
  buildingNumber: string;
}

const initialSelectedAddress: SelectedAddress = {
  country: '',
  countryIso: '',
  state: '',
  town: '',
  street: '',
  postcode: '',
  unitNumber: '',
  buildingNumber: '',
};

function parseAddressComponents(components: google.maps.GeocoderAddressComponent[]): SelectedAddress {
  return components.reduce<SelectedAddress>((acc, component) => {
    const { long_name: longName, short_name: shortName, types } = component;

    if (types.includes('country')) {
      return { ...acc, country: shortName, countryIso: shortName };
    }
    if (types.includes('administrative_area_level_1')) {
      return { ...acc, state: longName };
    }
    if (types.includes('locality') || types.includes('sublocality')) {
      return { ...acc, town: longName };
    }
    if (types.includes('route')) {
      return { ...acc, street: longName };
    }
    if (types.includes('postal_code')) {
      return { ...acc, postcode: longName };
    }
    if (types.includes('subpremise')) {
      return { ...acc, unitNumber: longName };
    }
    if (types.includes('street_number')) {
      return { ...acc, buildingNumber: longName };
    }
    return acc;
  }, initialSelectedAddress);
}

export const GooglePlaces = forwardRef<HTMLInputElement, GooglePlacesProps>(
  (
    {
      className,
      inputFieldClassName,
      size = 'medium',
      label,
      labelClassName,
      isFilled,
      placeholder,
      onChange,
      onInput,
      inputExternalProps,
      onSuccess,
      onError,
      isDisabled,
      availableCountry,
      ...props
    }: GooglePlacesProps,
    ref,
  ) => {
    const { isLoaded } = useLoadScript({
      googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? '',
      libraries: LIBRARIES,
    });
    const [address, setAddress] = useState('');
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

    useEffect(() => {
      if (typeof props.value === 'string') {
        setAddress(props.value);
      }
    }, [props.value]);

    function onPlaceChanged() {
      try {
        const place = autocompleteRef.current?.getPlace();

        if (!place?.address_components) {
          onError?.('Error fetching address details');
          return;
        }

        const fullAddress = place.formatted_address ?? '';
        setAddress(fullAddress);
        onSuccess?.({ fullAddress, ...parseAddressComponents(place.address_components) });
      } catch (error) {
        onError?.('Error fetching address details');
      }
    }

    return (
      <div className={clsx(styles.wrapper, styles[size], className)}>
        {!!label && (
          <label
            className={clsx(styles.label, labelClassName, {
              [styles.labelLarge]: size === 'large',
            })}
          >
            {label}
          </label>
        )}
        <div
          className={clsx(styles.inputField, inputFieldClassName, {
            [styles.filled]: props.value || isFilled,
            [styles.inputFieldLarge]: size === 'large',
          })}
        >
          {isLoaded ? (
            <Autocomplete
              className={styles.autocomplete}
              onLoad={(autocomplete) => {
                autocompleteRef.current = autocomplete;
              }}
              onPlaceChanged={onPlaceChanged}
              options={{
                fields: ['address_components', 'formatted_address'],
                types: ['address'],
                componentRestrictions: availableCountry
                  ? { country: availableCountry.toLowerCase() }
                  : undefined,
              }}
            >
              <input
                className={clsx(styles.input, { [styles.inputLarge]: size === 'large' })}
                ref={ref}
                aria-label="input"
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                readOnly={props.isReadOnly || inputExternalProps?.readOnly}
                placeholder={placeholder}
                disabled={isDisabled}
              />
            </Autocomplete>
          ) : (
            <input
              className={clsx(styles.input, { [styles.inputLarge]: size === 'large' })}
              disabled
              aria-label="input"
              placeholder={placeholder}
            />
          )}
        </div>
      </div>
    );
  },
);

GooglePlaces.displayName = 'GooglePlaces';
